// DTOs of the schema admin API (M7): item types, workflows and statuses.
// Label groups ("Fields") have their own DTOs next to their mutations (core mutations/labels.ts).
import { z } from "zod";
import { StatusCategory } from "./items.ts";
import { LABEL_COLORS } from "./labels.ts";
import type { Localized } from "./localized.ts";
import { LocalizedText } from "./translation.ts";

/**
 * Phosphor icon names an item type may use. Mirrors `TYPE_ICONS` in apps/web/src/components/domain/TypeIcon.tsx
 * (that registry is what the web can render): add a name in both places.
 */
export const ITEM_TYPE_ICONS = [
  "Flag",
  "Rocket",
  "Stack",
  "Lightning",
  "Bug",
  "CheckSquare",
  "ListChecks",
  "Lightbulb",
  "Target",
  "Compass",
  "Cube",
  "Wrench",
  "BookOpen",
  "Question",
  "ChatCircle",
  "GearSix",
  "Shield",
  "Star",
  "Heart",
  "Package",
  "Palette",
  "Code",
  "Database",
  "Globe",
  "Calendar",
  "Flask",
  "Fire",
  "Lock",
  "TreeStructure",
  "Diamond",
  "Hexagon",
  "Square",
  "Circle",
  "Kanban",
  "ArrowsSplit",
  "GitBranch",
  "Plugs",
  "Sparkle",
] as const;
export const ItemTypeIcon = z.enum(ITEM_TYPE_ICONS);
export type ItemTypeIcon = z.infer<typeof ItemTypeIcon>;

/** 3 initiative, 2 epic, 1 standard (task, bug, feature), 0 sub-task. A parent's level must exceed its child's. */
export const ITEM_TYPE_LEVELS = [3, 2, 1, 0] as const;
export const MAX_ITEM_TYPE_LEVEL = 3;

/** Colour tokens of item types: the label palette. */
export const ItemTypeColor = z.enum(LABEL_COLORS);

/** Status colours: the label palette plus `slate`, which the seeded "Todo" status uses. */
export const STATUS_COLORS = [...LABEL_COLORS, "slate"] as const;
export const StatusColor = z.enum(STATUS_COLORS);
export type StatusColor = z.infer<typeof StatusColor>;

/** Active (non-archived) statuses per workflow. */
export const MAX_STATUSES_PER_WORKFLOW = 30;

const Slug = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9._-]*$/, "lowercase letters, digits, dot, dash or underscore");

/** Display name: English required, German optional (an empty German name is dropped, readers fall back to English). */
export const AdminName = z
  .object({ en: z.string().trim().min(1).max(80), de: z.string().trim().max(80).optional() })
  .transform((n): Localized => (n.de ? { en: n.en, de: n.de } : { en: n.en }));

/** Description template per content language; empty variants are dropped, `null` (or all empty) = no template. */
export const AdminTemplate = LocalizedText.extend({
  en: z.string().max(20_000).optional(),
  de: z.string().max(20_000).optional(),
}).transform((t) => {
  const out: { en?: string; de?: string } = {};
  if (t.en?.trim()) out.en = t.en;
  if (t.de?.trim()) out.de = t.de;
  return Object.keys(out).length > 0 ? out : null;
});

const TypeIds = z.array(z.uuid()).max(50);

// ---------------------------------------------------------------------------------------------------
// Item types (schema.manage)

export const ItemTypeCreate = z.object({
  /** Derived from the English name when omitted (suffixed -2, -3, ... until free). */
  slug: Slug.optional(),
  name: AdminName,
  icon: ItemTypeIcon,
  color: ItemTypeColor.default("gray"),
  level: z.number().int().min(0).max(MAX_ITEM_TYPE_LEVEL).default(1),
  /** Types this one may sit under. Empty = any type with a higher level. Each must have a higher level. */
  allowedParentTypeIds: TypeIds.default([]),
  templateMd: AdminTemplate.nullish(),
  /** Defaults to the end. */
  position: z.number().int().optional(),
});
export type ItemTypeCreate = z.input<typeof ItemTypeCreate>;

export const ItemTypePatch = z.object({
  name: AdminName.optional(),
  icon: ItemTypeIcon.optional(),
  color: ItemTypeColor.optional(),
  level: z.number().int().min(0).max(MAX_ITEM_TYPE_LEVEL).optional(),
  allowedParentTypeIds: TypeIds.optional(),
  templateMd: AdminTemplate.nullish(),
  position: z.number().int().optional(),
  /** true archives the type (no new items; existing items keep it), false restores it. */
  archived: z.boolean().optional(),
  /** Custom types only (built-in slugs are fixed). Saved views, the API and agents refer to slugs: change sparingly. */
  slug: Slug.optional(),
});
export type ItemTypePatch = z.input<typeof ItemTypePatch>;

export const ItemTypeReorder = z.object({ typeIds: z.array(z.uuid()).max(200) });
export type ItemTypeReorder = z.infer<typeof ItemTypeReorder>;

export const ItemTypeMigrate = z.object({
  toTypeId: z.uuid(),
  /** Query-language filter on top of "items of this type" (e.g. `product:PLZ is:open`). Omitted = all items of the type. */
  query: z.string().trim().max(500).optional(),
  /** Report what would happen without changing anything. */
  dryRun: z.boolean().default(false),
});
export type ItemTypeMigrate = z.input<typeof ItemTypeMigrate>;

// ---------------------------------------------------------------------------------------------------
// Workflows and statuses

export const StatusInput = z.object({
  /** Derived from the English name when omitted (suffixed -2, -3, ... until free in the workflow). */
  slug: Slug.optional(),
  name: AdminName,
  category: StatusCategory,
  color: StatusColor.default("gray"),
});
export type StatusInput = z.input<typeof StatusInput>;

export const StatusCreate = StatusInput.extend({
  /** Index among the workflow's active statuses (0 = first). Defaults to the end. */
  position: z.number().int().min(0).optional(),
});
export type StatusCreate = z.input<typeof StatusCreate>;

export const WorkflowCreate = z.object({
  slug: Slug.optional(),
  name: AdminName,
  /** Copies the statuses (slugs, names, categories, colours, order; not archived ones) of this workflow. */
  cloneFromWorkflowId: z.uuid().optional(),
  /** Initial statuses when not cloning. Omitted = Backlog, In progress, Done, Canceled. */
  statuses: z.array(StatusInput).min(1).max(MAX_STATUSES_PER_WORKFLOW).optional(),
});
export type WorkflowCreate = z.input<typeof WorkflowCreate>;

export const WorkflowPatch = z.object({
  name: AdminName.optional(),
  /** true archives the workflow (refused while products use it, or for the built-in one). */
  archived: z.boolean().optional(),
  slug: z.unknown().optional(),
});
export type WorkflowPatch = z.input<typeof WorkflowPatch>;

export const StatusPatch = z.object({
  name: AdminName.optional(),
  color: StatusColor.optional(),
  /** Allowed while the workflow keeps an open (backlog/unstarted) and a completed status. */
  category: StatusCategory.optional(),
  /** Index among the workflow's active statuses. */
  position: z.number().int().min(0).optional(),
  /** Archiving a status that items still use is refused (409 `status_in_use`): use DELETE with `moveItemsTo`. */
  archived: z.boolean().optional(),
  slug: z.unknown().optional(),
});
export type StatusPatch = z.input<typeof StatusPatch>;

export const StatusReorder = z.object({ statusIds: z.array(z.uuid()).max(200) });
export type StatusReorder = z.infer<typeof StatusReorder>;

/** Body (or `?moveItemsTo=`) of DELETE /statuses/:id. Required while any item (even deleted/archived) uses the status. */
export const StatusRemove = z.object({ moveItemsTo: z.uuid().optional() });
export type StatusRemove = z.infer<typeof StatusRemove>;

export const ProductWorkflowSwitch = z.object({
  workflowId: z.uuid(),
  /** `{ fromStatusId: toStatusId }` for every status that items of the product use. */
  statusMapping: z.record(z.uuid(), z.uuid()).default({}),
});
export type ProductWorkflowSwitch = z.input<typeof ProductWorkflowSwitch>;

// ---------------------------------------------------------------------------------------------------
// Views returned by the admin API

export interface ItemTypeAdminView {
  id: string;
  slug: string;
  name: Localized;
  icon: string;
  color: string;
  level: number;
  allowedParentTypeIds: string[];
  templateMd: { en?: string; de?: string } | null;
  position: number;
  builtin: boolean;
  archived: boolean;
  /** The type new items get when none is named; cannot be archived or deleted. */
  isDefault: boolean;
  /** All items of the type, deleted and archived ones included; null without schema.manage. */
  itemCount: number | null;
  /** Whether the caller may change it. */
  canManage: boolean;
}

export interface StatusAdminView {
  id: string;
  workflowId: string;
  slug: string;
  name: Localized;
  category: StatusCategory;
  color: string;
  position: number;
  archived: boolean;
  /** The status new items get (first backlog status, else first unstarted, else first by position). */
  isDefault: boolean;
  /** All items in the status, deleted and archived ones included; null without schema.manage. */
  itemCount: number | null;
}

export interface WorkflowAdminView {
  id: string;
  slug: string;
  name: Localized;
  builtin: boolean;
  archived: boolean;
  /** Ordered by position; archived statuses last (and only listed for schema.manage). */
  statuses: StatusAdminView[];
  /** Products using the workflow that the caller can read. */
  products: { id: string; key: string; name: string; spaceId: string }[];
  /** Products using it that the caller cannot read (private products); counted only. */
  hiddenProductCount: number;
  canManage: boolean;
}

/** Details of the 409 `hierarchy_conflict` of an item type update. */
export interface HierarchyConflictDetails {
  count: number;
  /** At most 10 offending parent/child pairs. */
  samples: { childKey: string; parentKey: string; childTypeId: string; parentTypeId: string }[];
}
